import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Stack, Typography, type SxProps, type Theme } from '@mui/material';
import { tokens } from '../theme';

/** Centered content column used by every section (1240px max, 32px gutters on desktop). */
export function Section({
  children,
  band,
  py = { xs: 6, md: 12 },
  sx,
  id,
}: {
  children: ReactNode;
  /** Full-width background behind the column. */
  band?: 'subtle' | 'tint' | 'brand';
  py?: number | Record<string, number>;
  sx?: SxProps<Theme>;
  id?: string;
}) {
  const bg =
    band === 'subtle' ? tokens.subtle : band === 'tint' ? tokens.tint : band === 'brand' ? tokens.blue : undefined;
  return (
    <Box
      component="section"
      id={id}
      sx={{
        bgcolor: bg,
        color: band === 'brand' ? '#fff' : undefined,
        borderTop: band === 'subtle' ? `1px solid ${tokens.divider}` : undefined,
        borderBottom: band === 'subtle' ? `1px solid ${tokens.divider}` : undefined,
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 4 }, py, ...sx }}>{children}</Box>
    </Box>
  );
}

/** Grey title band with breadcrumb at the top of inner pages. */
export function PageHeader({
  title,
  subtitle,
  crumbs = [],
  aside,
  artwork,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  crumbs?: Array<{ label: string; to?: string }>;
  aside?: ReactNode;
  artwork?: string;
  /** Extra content under the title row, inside the band. */
  children?: ReactNode;
}) {
  return (
    <Section band="subtle" py={{ xs: 5, md: 7 }} sx={{ borderTop: 0, position: 'relative', overflow: 'hidden' }}>
      {artwork && (
        <Box
          component="img"
          src={artwork}
          alt=""
          aria-hidden="true"
          sx={{
            position: 'absolute',
            zIndex: 0,
            top: 0,
            right: { xs: -100, sm: -40, md: -20 },
            width: { xs: 330, sm: 480, md: 650 },
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'right center',
            opacity: { xs: 0.24, sm: 0.48, md: 0.92 },
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={4}
        alignItems={{ md: 'flex-end' }}
        justifyContent="space-between"
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Breadcrumbs aria-label="Breadcrumb" sx={{ fontSize: 14, color: 'text.secondary' }}>
            <Link component={RouterLink} to="/" fontWeight={500}>
              Home
            </Link>
            {crumbs.map((c) =>
              c.to ? (
                <Link key={c.label} component={RouterLink} to={c.to} fontWeight={500}>
                  {c.label}
                </Link>
              ) : null,
            )}
            <Typography fontSize={14} color="text.secondary">
              {title}
            </Typography>
          </Breadcrumbs>
          <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 52 } }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: { xs: 18, md: 20 }, color: 'text.secondary', maxWidth: 640 }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
        {aside}
      </Stack>
      {children && <Box sx={{ position: 'relative', zIndex: 1, mt: { xs: 3.5, md: 4.5 } }}>{children}</Box>}
    </Section>
  );
}

const appLogos: Record<string, string> = {
  'astrix-slide': '/app-logos/astrix-slide.svg',
  'ast-it-hub': '/app-logos/ast-it-hub.svg',
  traceboard: '/app-logos/traceboard.svg',
  'ast-queryhub': '/app-logos/ast-queryhub.svg',
  'ast-oneid': '/app-logos/ast-oneid.svg',
  'netsuite-mes-integration': '/app-logos/netsuite-mes-integration.svg',
  'ast-cmdb': '/app-logos/ast-cmdb.svg',
  'ast-audit-cockpit': '/app-logos/ast-audit-cockpit.svg',
  'ast-mes-assistant': '/app-logos/ast-mes-assistant.svg',
  'ast-production-control': '/app-logos/ast-production-control.svg',
  'ast-costing': '/app-logos/ast-costing.svg',
  'ast-procurement-control': '/app-logos/ast-procurement-control.svg',
  'ast-payment-request': '/app-logos/ast-payment-request.svg',
  astrixgpt: '/app-logos/astrixgpt.svg',
};

/** App identity tile, using a product logo when one is available. */
export function AppMonogram({
  name,
  slug,
  logoUrl,
  size = 'md',
}: {
  name: string;
  slug?: string;
  logoUrl?: string | null;
  size?: 'sm' | 'md' | 'hero';
}) {
  const words = name.split(/\s+/).filter((w) => /[A-Za-z]/.test(w[0] ?? ''));
  const initials = (
    words.length > 1 ? words.slice(-2).map((w) => w[0]).join('') : (words[0] ?? '?').slice(0, 2)
  ).toUpperCase();
  const logo = (slug ? appLogos[slug] : undefined) || logoUrl;
  if (size === 'sm') {
    return (
      <Box
        aria-hidden="true"
        sx={{
          width: 40,
          height: 40,
          flex: 'none',
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          fontWeight: 700,
          fontSize: 14,
          overflow: 'hidden',
          bgcolor: logo ? '#fff' : 'primary.main',
          color: '#fff',
        }}
      >
        {logo ? <Box component="img" src={logo} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
      </Box>
    );
  }
  return (
    <Box
      aria-hidden="true"
      sx={{
        aspectRatio: size === 'hero' ? '16 / 9' : '16 / 10',
        borderRadius: 2,
        bgcolor: logo ? '#fff' : tokens.tint,
        color: 'primary.main',
        display: 'grid',
        placeItems: 'center',
        fontWeight: 800,
        letterSpacing: '-0.03em',
        fontSize: size === 'hero' ? 56 : 40,
        overflow: 'hidden',
      }}
    >
      {logo ? <Box component="img" src={logo} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
    </Box>
  );
}
